import React, { Fragment } from "react";
import { inject, observer } from "mobx-react";
import { Button, Grid, Typography, IconButton, Tooltip } from "@material-ui/core";
import SectionHeader from "../common/SectionHeader";
import TextField from "@material-ui/core/TextField";
import Loading from "../loadingSpinner/Loading";
import { withRouter } from "react-router-dom";
import { MuiPickersUtilsProvider, DatePicker } from "material-ui-pickers";
import moment from "moment";
import MomentUtils from "@date-io/moment";
import EventIcon from "@material-ui/icons/Event";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";
import CloudDownload from "@material-ui/icons/CloudDownload";
import DeleteForever from "@material-ui/icons/DeleteForever";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import AddIcon from "@material-ui/icons/Add";
import DescriptionIcon from "@material-ui/icons/Description";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import CloseIcon from "@material-ui/icons/Close";
import ConfirmationModal from "../modal/ConfirmationModal";
import AddDocumentModal from "./AddDocumentModal";
import Dropzone from "react-dropzone";
import "./Documents.css";

const FILE_MAXSIZE = 8497152;
const ACCEPTED_FILES = "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png";

@inject("documentsProfileStore", "templateStore", "authStore", "profileStore")
@withRouter
@observer
class Documents extends React.Component {
    state = {
        confirmDeleteDocumentState: false,
        documentToDelete: null,
        isLoading: true,
        insuranceFile: null
    };

    componentDidMount() {
        window.scrollTo(0, 0);
        this.props.documentsProfileStore.setType(this.props.templateStore.getType(this.props.type));
        this.props.documentsProfileStore
            .loadInitialData(this.props.profileId)
            .then(() => { })
            .finally(() => {
                this.setState({ isLoading: false });
                window.scrollTo(0, 0);
            });
    }

    handleInsurancePolicyHolderChange = e => this.props.documentsProfileStore.setInsurancePolicyHolder(e.target.value);
    handleInsuranceCompanyChange = e => this.props.documentsProfileStore.setInsuranceCompany(e.target.value);
    handleInsurancePolicyNumberChange = e => this.props.documentsProfileStore.setInsurancePolicyNumber(e.target.value);
    handleInsuranceExpiryDateChange = e => this.props.documentsProfileStore.setInsuranceExpiryDate(moment(e));

    handleInsuranceFileDrop = (acceptedFiles, rejectedFiles) => {
        if (rejectedFiles && rejectedFiles.length > 0) {
            this.props.templateStore.openSnackbar("File couldn't be uploaded. Please check size (max 8MB) and format.");
            return;
        }
        if (acceptedFiles && acceptedFiles.length > 0) {
            this.setState({ insuranceFile: acceptedFiles[0] });
        }
    };

    handleRemoveInsuranceFile = () => {
        this.setState({ insuranceFile: null });
    };

    handleDownloadDocument = (document) => {
        this.props.documentsProfileStore.downloadDocument(document);
    };

    handleDownloadInsuranceFile = () => {
        const { insuranceFileName, insuranceFileContent } = this.props.documentsProfileStore;
        if (insuranceFileContent) {
            this.props.documentsProfileStore.downloadInsuranceFile();
        }
    };

    handleDeleteInsuranceFile = () => {
        this.props.documentsProfileStore.deleteInsuranceFile();
    };

    handleOpenDeleteConfirmation = (document) => {
        this.setState({
            confirmDeleteDocumentState: true,
            documentToDelete: document
        });
    };

    handleCloseDeleteConfirmation = () => {
        this.setState({
            confirmDeleteDocumentState: false,
            documentToDelete: null
        });
    };

    handleDeleteDocument = () => {
        const { documentToDelete } = this.state;
        if (documentToDelete) {
            this.props.documentsProfileStore.deleteDocument(documentToDelete).then(() => {
                this.handleCloseDeleteConfirmation();
            });
        }
    };

    handleOpenAddDocumentModal = () => {
        this.props.documentsProfileStore.openAddDocumentModal();
    };

    handleCloseAddDocumentModal = () => {
        this.props.documentsProfileStore.closeAddDocumentModal();
    };

    handleBack = () => {
        if (this.props.type === "venue") {
            const { profile } = this.props.profileStore;
            this.props.history.push(`/venues/${profile.id}/edit/favourite`);
        }
        const { editProfileActiveStep } = this.props.templateStore;
        this.props.templateStore.setEditProfileActiveStep(editProfileActiveStep - 1);
    };

    handleSubmitForm = e => {
        e.preventDefault();
        this.setState({ isLoading: true });
        var currentProfile = this.props.profileStore.getCurrentProfile();
        const { insuranceFile } = this.state;
        this.props.documentsProfileStore.submit(currentProfile?.id, insuranceFile).then(resp => {
            this.setState({ insuranceFile: null });
            if (this.props.type === "venue") {
                this.props.history.push(`/venues/${resp.id}/edit/contract`);
            }
            const { editProfileActiveStep } = this.props.templateStore;
            this.props.templateStore.setEditProfileActiveStep(editProfileActiveStep + 1);
        });
    };

    getFileIcon(fileName) {
        if (!fileName) return "fa-file";
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf':
                return "fa-file-pdf";
            case 'doc':
            case 'docx':
                return "fa-file-word";
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return "fa-file-image";
            default:
                return "fa-file";
        }
    }

    getFileIconColor(fileName) {
        if (!fileName) return "#666";
        const ext = fileName.split('.').pop().toLowerCase();
        switch (ext) {
            case 'pdf':
                return "#e53935";
            case 'doc':
            case 'docx':
                return "#1976d2";
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
                return "#43a047";
            default:
                return "#666";
        }
    }

    formatFileSize(bytes) {
        if (!bytes) return "";
        if (bytes < 1024) return bytes + " B";
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
        return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    }

    getInsuranceStatus() {
        const { insuranceExpiryDate } = this.props.documentsProfileStore;
        if (!insuranceExpiryDate) return { label: "NOT SET", className: "missing" };
        const expiryMoment = moment(insuranceExpiryDate);
        if (expiryMoment.isBefore(moment())) {
            return { label: "EXPIRED", className: "expired" };
        }
        return { label: "ACTIVE", className: "active" };
    }

    render() {
        const {
            generalDocuments,
            insurancePolicyHolder,
            insuranceCompany,
            insurancePolicyNumber,
            insuranceExpiryDate,
            addDocumentModalOpen,
            insuranceFileName,
            insuranceFileContent,
            insuranceFileGuid
        } = this.props.documentsProfileStore;
        const { editProfileActiveStep } = this.props.templateStore;
        const type = this.props.type;
        const steps = this.props.templateStore.getSteps(type);
        const { isLoading, insuranceFile } = this.state;
        const insuranceStatus = this.getInsuranceStatus();
        const hasExistingInsuranceFile = insuranceFileName && (insuranceFileContent || insuranceFileGuid);

        return (
            <div className="Profile-Documents">
                <form onSubmit={this.handleSubmitForm} className="form-relative">
                    <Loading showed={isLoading} />
                    <Grid container spacing={24}>
                        <Grid item xs={12} lg={12}>
                            <div className="documents-header">
                                <SectionHeader
                                    title="Documents"
                                    helpText="Upload and manage your documents here. Insurance certificates, technical riders, stage plots, press kits, and other important documents can be shared with agents and venues."
                                />
                            </div>

                            <div className="insurance-card">
                                <div className="insurance-card-header">
                                    <h3>
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                            <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                                        </svg>
                                        Public Liability Insurance
                                    </h3>
                                    <span className={`status-badge ${insuranceStatus.className}`}>
                                        {insuranceStatus.label}
                                    </span>
                                </div>
                                <div className="insurance-card-body">
                                    <Typography variant="body2" color="textSecondary" className="info-text">
                                        Some venues require Public Liability Insurance. Enter your policy details and upload your certificate below.
                                    </Typography>
                                    
                                    <Grid container spacing={16}>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                id="insurancePolicyHolder"
                                                label="Policy Holder Name"
                                                margin="dense"
                                                fullWidth
                                                value={insurancePolicyHolder}
                                                onChange={this.handleInsurancePolicyHolderChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                id="insuranceCompany"
                                                label="Insurance Company"
                                                margin="dense"
                                                fullWidth
                                                value={insuranceCompany}
                                                onChange={this.handleInsuranceCompanyChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                id="insurancePolicyNumber"
                                                label="Policy Number"
                                                margin="dense"
                                                fullWidth
                                                value={insurancePolicyNumber}
                                                onChange={this.handleInsurancePolicyNumberChange}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <MuiPickersUtilsProvider utils={MomentUtils}>
                                                <DatePicker
                                                    id="insurance-expiry-date"
                                                    className="booking-date-picker"
                                                    keyboard
                                                    value={insuranceExpiryDate || null}
                                                    fullWidth
                                                    margin="dense"
                                                    onChange={this.handleInsuranceExpiryDateChange}
                                                    label="Expiry Date"
                                                    format="DD-MM-YYYY"
                                                    keyboardIcon={<EventIcon />}
                                                    leftArrowIcon={<KeyboardArrowLeftIcon />}
                                                    rightArrowIcon={<KeyboardArrowRightIcon />}
                                                />
                                            </MuiPickersUtilsProvider>
                                        </Grid>
                                    </Grid>

                                    <div className="file-upload-section">
                                        <Typography variant="subtitle2" className="upload-section-title">
                                            Insurance Certificate
                                        </Typography>
                                        
                                        {hasExistingInsuranceFile && !insuranceFile ? (
                                            <div className="uploaded-file">
                                                <div className="uploaded-file-info">
                                                    <InsertDriveFileIcon className="file-icon" />
                                                    <div className="uploaded-file-details">
                                                        <h5>{insuranceFileName}</h5>
                                                        <span>Uploaded</span>
                                                    </div>
                                                </div>
                                                <div className="uploaded-file-actions">
                                                    <Tooltip title="Download">
                                                        <IconButton onClick={this.handleDownloadInsuranceFile} className="icon-btn">
                                                            <CloudDownload />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete">
                                                        <IconButton onClick={this.handleDeleteInsuranceFile} className="icon-btn delete">
                                                            <DeleteForever />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        ) : insuranceFile ? (
                                            <div className="uploaded-file new">
                                                <div className="uploaded-file-info">
                                                    <InsertDriveFileIcon className="file-icon" />
                                                    <div className="uploaded-file-details">
                                                        <h5>{insuranceFile.name}</h5>
                                                        <span>{this.formatFileSize(insuranceFile.size)} - Ready to upload</span>
                                                    </div>
                                                </div>
                                                <div className="uploaded-file-actions">
                                                    <Tooltip title="Remove">
                                                        <IconButton onClick={this.handleRemoveInsuranceFile} className="icon-btn delete">
                                                            <CloseIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                        ) : (
                                            <Dropzone
                                                accept={ACCEPTED_FILES}
                                                maxSize={FILE_MAXSIZE}
                                                onDrop={this.handleInsuranceFileDrop}
                                                className="upload-area"
                                            >
                                                <CloudUploadIcon className="upload-icon" />
                                                <p>Drop your certificate here or click to browse</p>
                                                <span>PDF, DOC, DOCX, JPG, PNG (max 8MB)</span>
                                            </Dropzone>
                                        )}
                                    </div>

                                    <div className="save-section">
                                        <Button variant="contained" color="primary" type="submit">
                                            SAVE INSURANCE DETAILS
                                        </Button>
                                    </div>
                                </div>
                            </div>

                            <div className="documents-grid-section">
                                <div className="other-documents-header">
                                    <Typography variant="subtitle1" className="section-subtitle">
                                        Other Documents
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={this.handleOpenAddDocumentModal}
                                        className="add-document-btn"
                                    >
                                        <AddIcon /> Add Document
                                    </Button>
                                </div>
                                
                                {generalDocuments.length === 0 ? (
                                    <div className="no-documents">
                                        <DescriptionIcon style={{ fontSize: 48, color: '#ccc' }} />
                                        <Typography variant="body1" color="textSecondary">
                                            No documents uploaded yet
                                        </Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Click "Add Document" to upload your first document
                                        </Typography>
                                    </div>
                                ) : (
                                    <div className="documents-grid">
                                        {generalDocuments.map((doc, index) => (
                                            <div 
                                                key={doc.id || index} 
                                                className={`document-card ${doc.documentType}`}
                                            >
                                                <div className="document-card-header">
                                                    <h4>
                                                        <i className={`fa ${this.getFileIcon(doc.fileName)}`}></i>
                                                        {doc.title}
                                                    </h4>
                                                </div>
                                                <div className="document-card-body">
                                                    <div 
                                                        className="doc-icon"
                                                        style={{ backgroundColor: this.getFileIconColor(doc.fileName) }}
                                                    >
                                                        <i className={`fa ${this.getFileIcon(doc.fileName)}`}></i>
                                                    </div>
                                                    <div className="doc-info">
                                                        <h5>{doc.fileName || doc.title}</h5>
                                                        <span>
                                                            {doc.createdAt && `Uploaded ${moment(doc.createdAt).format('MMM D, YYYY')}`}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="document-card-actions">
                                                    <button 
                                                        type="button" 
                                                        className="action-btn"
                                                        onClick={() => this.handleDownloadDocument(doc)}
                                                    >
                                                        <CloudDownload style={{ fontSize: 16 }} />
                                                        Download
                                                    </button>
                                                    <button 
                                                        type="button" 
                                                        className="action-btn"
                                                        onClick={() => this.handleOpenDeleteConfirmation(doc)}
                                                    >
                                                        <DeleteForever style={{ fontSize: 16 }} />
                                                        Delete
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Grid>

                        <Grid item xs={12}>
                            <div className="action">
                                <Button disabled={editProfileActiveStep === 0} onClick={this.handleBack}>
                                    Back
                                </Button>
                                <Button variant="contained" color="primary" type="submit">
                                    {editProfileActiveStep === steps.length - 1 ? "Finish" : "Save/Next"}
                                </Button>
                            </div>
                        </Grid>
                    </Grid>
                </form>

                <AddDocumentModal
                    open={addDocumentModalOpen}
                    onClose={this.handleCloseAddDocumentModal}
                    profileId={this.props.profileId}
                />

                <ConfirmationModal
                    open={this.state.confirmDeleteDocumentState}
                    title="Delete Document"
                    message={`Are you sure you want to delete "${this.state.documentToDelete?.title || 'this document'}"?`}
                    closeHandler={this.handleCloseDeleteConfirmation}
                    confirmationHandler={this.handleDeleteDocument}
                    declineHandler={this.handleCloseDeleteConfirmation}
                    confirmationLabel="Delete"
                    declineLabel="Cancel"
                />
            </div>
        );
    }
}

export default Documents;
